// Lightweight hash-based router. A later follow-up can swap this for
// `react-router-dom` (v6) without touching feature code — the primitives
// below intentionally mirror a subset of that API:
//   - <RouterProvider> subscribes to `window.location.hash`.
//   - useRoute() returns the current route string ('', 'dashboard', 'admin',
//     'terms', 'about', 'testimonials', 'learn').
//   - navigate(route) updates the hash.
//
// Plan 20 keeps URL shape identical to the pre-refactor monolith so that
// plan 43's e2e tests and any inbound deep-links continue to work:
//   #              → landing
//   #dashboard     → dashboard
//   #admin         → admin
//   #terms         → terms (also #privacy-policy, #birth-data-disclaimer)
//   #about         → about
//   #testimonials  → testimonials/submit
//   #learn         → learn more page
//   #contact       → contact me page (plan 76)
//   #contribute    → contribution / tip-jar page

import React, { createContext, useContext, useEffect, useState } from 'react';
import { hardNavigate } from '../shared/utils/hardNavigate';

export type RouteName =
  | 'landing'
  | 'dashboard'
  | 'admin'
  | 'terms'
  | 'about'
  | 'testimonials'
  | 'learn'
  | 'contact'
  | 'contribute'
  // Plan 122 — public gift-certificate purchase page.
  | 'gift'
  // Plan 120 — public Network, Referrals & Recommendations page.
  | 'network'
  // Migration 063 — public per-product detail page (`#services/<slug>`).
  | 'service-detail'
  // Color-animation tuning tool (see src/features/shimmer-lab). Permanent.
  | 'shimmer-lab'
  // Typography tuning tool (see src/features/font-lab). Permanent.
  | 'font-lab';

export interface RouterState {
  route: RouteName;
  hash: string;
  /**
   * Migration 063 — when `route === 'service-detail'`, the decoded product
   * slug parsed from `#services/<slug>`. Empty string for every other route.
   */
  serviceSlug: string;
}

const RouterContext = createContext<RouterState>({ route: 'landing', hash: '', serviceSlug: '' });

const SERVICE_HASH_PREFIX = '#services/';

/** Decode the product slug out of a `#services/<slug>` hash (query stripped). */
export function serviceSlugFromHash(hash: string): string {
  const path = hash.split('?')[0] ?? '';
  if (!path.startsWith(SERVICE_HASH_PREFIX)) return '';
  try {
    return decodeURIComponent(path.slice(SERVICE_HASH_PREFIX.length));
  } catch {
    return path.slice(SERVICE_HASH_PREFIX.length);
  }
}

/** Navigate to a product detail page. `navigate()` only takes top-level route
 *  names, so the slug route needs its own helper. */
export function navigateToService(slug: string): void {
  const nextHash = `${SERVICE_HASH_PREFIX}${encodeURIComponent(slug)}`;
  if (window.location.hash !== nextHash) {
    window.location.hash = nextHash;
  }
}

function hashToRoute(hash: string): RouteName {
  // Strip the optional query string so '#admin/audio-report-builder?x=1'
  // resolves the same as '#admin/audio-report-builder'.
  const path = hash.split('?')[0] ?? '';

  // Plan 208 — `#admin` grew nested sub-routes (e.g.
  // `#admin/audio-report-builder/new`). Match any path under the admin
  // namespace so nested routes still resolve to the admin top-level page;
  // the admin page itself parses the sub-path. Without this prefix match,
  // the strict-equality switch below falls through to 'landing' and the
  // whole app pivots to the homepage when a sub-route hash lands.
  if (path === '#admin' || path.startsWith('#admin/')) return 'admin';

  // Migration 063 — `#services/<slug>` resolves to the per-product detail
  // page; the page reads the slug via `serviceSlugFromHash`. Bare `#services`
  // (no slug) falls through to the landing page, which has the catalog.
  if (path.startsWith(SERVICE_HASH_PREFIX)) return 'service-detail';

  switch (path) {
    case '#dashboard':
      return 'dashboard';
    case '#terms':
    case '#privacy-policy':
    case '#birth-data-disclaimer':
      return 'terms';
    case '#about':
      return 'about';
    case '#testimonials':
      return 'testimonials';
    case '#learn':
      return 'learn';
    case '#contact':
      return 'contact';
    case '#contribute':
      return 'contribute';
    case '#gift':
      return 'gift';
    case '#network':
      return 'network';
    case '#shimmer-lab':
      return 'shimmer-lab';
    case '#font-lab':
      return 'font-lab';
    default:
      return 'landing';
  }
}

export function navigate(route: RouteName | '') {
  const nextHash = route === '' || route === 'landing' ? '' : `#${route}`;
  // Plan 124 — SPA components are also mounted inside the Astro catalog
  // island (e.g. /services/<slug>), where no hash router exists. A hash
  // change there would be a silent no-op, so escape with a full
  // navigation back to the SPA. The SPA itself always lives at '/'.
  if (window.location.pathname !== '/') {
    hardNavigate(`/${nextHash}`);
    return;
  }
  if (window.location.hash !== nextHash) {
    if (nextHash === '') {
      // Clear hash without leaving a trailing '#'.
      history.pushState('', document.title, window.location.pathname + window.location.search);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      window.location.hash = nextHash;
    }
  }
}

export const RouterProvider = ({ children }: { children: React.ReactNode }) => {
  const [hash, setHash] = useState<string>(() =>
    typeof window === 'undefined' ? '' : window.location.hash
  );

  useEffect(() => {
    const onHashChange = () => {
      setHash(window.location.hash);
      // Reproduce the monolith's scroll-to-section behaviour for legacy hashes.
      const h = window.location.hash;
      const scrollTarget =
        h === '#privacy-policy'
          ? 'terms-section-12'
          : h === '#birth-data-disclaimer'
            ? 'terms-section-9'
            : null;
      if (scrollTarget) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            document
              .getElementById(scrollTarget)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        });
      } else {
        const path = h.split('?')[0] ?? '';
        // Every route change opens at the top of the page. Only true
        // in-page section anchors keep native scroll-to-element behaviour
        // (plan 123 review: the previous route allowlist silently missed
        // '#about' and '#testimonials', which therefore kept the landing
        // page's scroll position).
        const isSectionAnchor = path === '' || path === '#' || path === '#services';
        if (!isSectionAnchor) window.scrollTo({ top: 0 });
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const value: RouterState = {
    route: hashToRoute(hash),
    hash,
    serviceSlug: serviceSlugFromHash(hash),
  };
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const useRoute = () => useContext(RouterContext);
